import React, { useEffect, useMemo, useRef, useState } from 'react'
import { buildPreviewDoc } from '../../utils/previewDoc'

// Renders the site's real home page as a scaled-down, non-interactive snapshot
// that fills the .site-thumb area and is cropped to the top of the page —
// effectively an always-fresh "screenshot" with zero maintenance. Returns null
// when there's no content so the card's gradient/logo fallback shows through.
//
// The page is rendered at a fixed desktop width and then CSS-scaled to fit the
// card, so the thumbnail looks the same regardless of card size. Scripts are
// NOT executed (sandbox omits allow-scripts) — keeps many cards cheap and safe.
const DESIGN_WIDTH = 1280

export default function SiteThumbPreview({ html, css, js }) {
  const wrapRef = useRef(null)
  const [scale, setScale] = useState(0)
  const [visible, setVisible] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const doc = useMemo(() => buildPreviewDoc(html, css, js), [html, css, js])

  // Only build the iframe once the card scrolls near the viewport.
  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); io.disconnect() }
    }, { rootMargin: '300px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Keep the scale in sync with the card width.
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const update = () => { if (el.clientWidth) setScale(el.clientWidth / DESIGN_WIDTH) }
    update()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!doc) return null

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1, background: 'white' }}
    >
      {visible && scale > 0 && (
        <iframe
          tabIndex={-1}
          title=""
          srcDoc={doc}
          sandbox="allow-same-origin"
          scrolling="no"
          loading="lazy"
          onLoad={() => setLoaded(true)}
          style={{
            width: DESIGN_WIDTH,
            // Tall enough that the crop always shows a full "above the fold"
            // band; overflow:hidden on the wrapper trims the rest.
            height: Math.round(DESIGN_WIDTH * 0.9),
            border: 'none',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            opacity: loaded ? 1 : 0,
            transition: 'opacity .3s ease',
          }}
        />
      )}
    </div>
  )
}
